import * as React from "react"
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@nexus/ui"
import { SetNameSchema, SET_NAME_MAX } from "@nexus/contracts"
import { useTranslation } from "react-i18next"

export interface RenameSetDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentName: string
  /** Names of other sets (excluding the one being renamed) used for uniqueness validation. */
  existingNames?: string[]
  onSubmit: (name: string) => void
  isSubmitting?: boolean
}

function validate(raw: string) {
  const result = SetNameSchema.safeParse(raw)
  if (result.success) return { value: result.data, error: null }
  return { value: raw.trim(), error: result.error.issues[0]?.message ?? "Invalid name" }
}

export function RenameSetDialog({ open, onOpenChange, currentName, existingNames = [], onSubmit, isSubmitting }: RenameSetDialogProps) {
  const { t } = useTranslation()
  const [name, setName] = React.useState(currentName)
  const [touched, setTouched] = React.useState(false)

  React.useEffect(() => {
    if (open) {
      setName(currentName)
      setTouched(false)
    }
  }, [open, currentName])

  const { value: trimmed, error: schemaError } = validate(name)
  const isDuplicate = !schemaError && existingNames.some((n) => n.toLowerCase() === trimmed.toLowerCase())
  const error = schemaError ?? (isDuplicate ? t("opportunities.setNameTaken", "A set with this name already exists.") : null)
  const showError = touched && !!error
  const isUnchanged = trimmed === currentName
  const charCount = trimmed.length
  const nearLimit = charCount >= SET_NAME_MAX * 0.9

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (error || isUnchanged) return
    onSubmit(trimmed)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{t("opportunities.renameSet", "Rename set")}</DialogTitle>
            <DialogDescription>{t("opportunities.renameSetDescription", "Give this opportunity set a new name.")}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5 py-4">
            <Label htmlFor="rename-set-name">{t("opportunities.setNameLabel")}</Label>
            <Input
              id="rename-set-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value)
                if (!touched) setTouched(true)
              }}
              onBlur={() => setTouched(true)}
              maxLength={SET_NAME_MAX}
              aria-invalid={showError || undefined}
              aria-describedby="rename-set-hint"
              autoFocus
            />
            <div className="flex items-center justify-between gap-2">
              <div
                id="rename-set-hint"
                role="alert"
                aria-live="polite"
                className={`grid transition-all duration-200 ease-out ${showError ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}
              >
                <span className="overflow-hidden text-xs text-destructive">{showError ? error : "\u00A0"}</span>
              </div>
              <span
                className={`shrink-0 text-xs tabular-nums transition-colors duration-150 ${
                  showError ? "text-destructive" : nearLimit ? "text-warning" : "text-muted-foreground"
                }`}
              >
                {charCount}/{SET_NAME_MAX}
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={showError || !!error || isUnchanged || isSubmitting}>
              {t("common.save", "Save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
